import { ESizeUnit } from 'bz-utilities/blobs/validate';
import React from 'react';
import { v4 as uuid } from 'uuid';
import { useRemoveTempFile } from './removeTempFile';
import { useUploadTempFile } from './uploadTempFile';

export interface FileEntry {
  key: string;
  name: string;
  status: 'uploading' | 'removing' | 'done' | 'error';
  errorMessage?: string;
}

export interface FileUploadData {
  tempFolderId: string;
  uploadedFileNames: string[];
}

export const DEFAULT_FILE_ACCEPT =
  '.bmp, .csv, .doc, .docx, .dot, .gif, .htm, .html, .jpe, .jpeg, .jpg, .mov, .mp4, .mpg, .odt, .pdf, .png, .ppt, .pptx, .rtf, .tif, .tiff, .txt, .webp, .wmv, .xls, .xlsx, .xml';
export const DEFAULT_FILE_MAX_SIZE_BYTES = ESizeUnit.MB * 10;

interface Options {
  isDisabled: boolean;
  data: FileUploadData | null;
  onUpdate: (data: FileUploadData) => void;
  fileEntries: FileEntry[];
  addFileEntry: (entry: FileEntry) => void;
  updateFileEntry: (key: string, patch: Partial<FileEntry>) => void;
  removeFileEntry: (key: string) => void;
}

export function useFileUploadState({
  isDisabled,
  data,
  onUpdate,
  fileEntries,
  addFileEntry,
  updateFileEntry,
  removeFileEntry
}: Options) {
  const tempFolderIdRef = React.useRef(data?.tempFolderId ?? uuid());
  const tempFolderId = tempFolderIdRef.current;
  const uploadFileToTemp = useUploadTempFile();
  const removeTempFile = useRemoveTempFile();

  const processFiles = async (selectedFiles: File[]) => {
    if (isDisabled || selectedFiles.length === 0) return;

    for (const file of selectedFiles) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      addFileEntry({ key, name: file.name, status: 'uploading' });

      try {
        const current = data ?? { tempFolderId, uploadedFileNames: [] };
        await uploadFileToTemp(file, tempFolderId);

        updateFileEntry(key, { status: 'done' });
        onUpdate({
          tempFolderId,
          uploadedFileNames: [...current.uploadedFileNames, file.name]
        });
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error ? err.message : 'Upload failed. Please try again.';
        updateFileEntry(key, { status: 'error', errorMessage });
      }
    }
  };

  const onDropRejected = (rejectedFiles: File[]) => {
    const accepted = DEFAULT_FILE_ACCEPT.split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    for (const file of rejectedFiles) {
      const key = `${file.name}-${Date.now()}-${Math.random()}`;
      const ext = ('.' + file.name.split('.').pop()).toLowerCase();
      const errorMessage =
        accepted.length > 0 && !accepted.includes(ext)
          ? `File type ${ext} is not allowed.`
          : `File is too large. Maximum size is ${DEFAULT_FILE_MAX_SIZE_BYTES / ESizeUnit.MB} MB.`;
      addFileEntry({ key, name: file.name, status: 'error', errorMessage });
    }
  };

  const onRemove = async (key: string) => {
    const entry = fileEntries.find((f) => f.key === key);
    if (!entry) return;

    if (entry.status !== 'done') {
      removeFileEntry(key);
      return;
    }

    updateFileEntry(key, { status: 'removing' });

    try {
      await removeTempFile(tempFolderId, entry.name);

      if (data) {
        onUpdate({
          tempFolderId,
          uploadedFileNames: data.uploadedFileNames.filter((n) => n !== entry.name)
        });
      }
      removeFileEntry(key);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Remove failed. Please try again.';
      updateFileEntry(key, { status: 'error', errorMessage });
    }
  };

  return { fileList: fileEntries, processFiles, onDropRejected, onRemove };
}
